import React, { createContext, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useSettings } from './SettingsContext';

// Types
export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

export interface ThemeConfig {
  colors: ThemeColors;
  fontFamily: string;
  borderRadius: string;
  boxShadow: string;
  fontSize: {
    xs: string;
    sm: string;
    base: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
  };
  animation: {
    duration: string;
    easing: string;
  };
}

// Default theme
const defaultTheme: ThemeConfig = {
  colors: {
    primary: '#3B82F6',
    secondary: '#1E40AF',
    accent: '#60A5FA',
    background: '#FFFFFF',
    surface: '#F8FAFC',
    text: '#1F2937',
    textSecondary: '#6B7280',
    border: '#E5E7EB',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
  },
  fontFamily: 'Times New Roman, serif',
  borderRadius: '8px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
  },
  animation: {
    duration: '0.3s',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

// Context
interface ThemeContextType {
  theme: ThemeConfig;
  getCSSVariables: () => Record<string, string>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Provider component
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { settings } = useSettings();
  const [themeConfig, setThemeConfig] = React.useState<ThemeConfig>(defaultTheme);

  // Update theme when settings change
  useEffect(() => {
    if (settings) {
      const newTheme = { ...defaultTheme };
      
      // Apply custom colors from settings
      if (settings.primaryColor) {
        newTheme.colors.primary = settings.primaryColor;
      }
      if (settings.secondaryColor) {
        newTheme.colors.secondary = settings.secondaryColor;
      }
      
      // Apply custom font family from settings
      if (settings.fontFamily) {
        newTheme.fontFamily = settings.fontFamily;
      }
      
      setThemeConfig(newTheme);
    }
  }, [settings]);

  // Get CSS variables function
  const getCSSVariables = useCallback((): Record<string, string> => {
    const { colors, fontSize, spacing, animation } = themeConfig;
    return {
      '--color-primary': colors.primary,
      '--color-primary-dark': adjustColor(colors.primary, -20),
      '--color-primary-light': adjustColor(colors.primary, 20),
      '--color-secondary': colors.secondary,
      '--color-accent': colors.accent,
      '--color-background': colors.background,
      '--color-surface': colors.surface,
      '--color-text': colors.text,
      '--color-text-secondary': colors.textSecondary,
      '--color-border': colors.border,
      '--color-success': colors.success,
      '--color-warning': colors.warning,
      '--color-error': colors.error,
      '--color-info': colors.info,
      '--font-family': themeConfig.fontFamily,
      '--font-size-xs': fontSize.xs,
      '--font-size-sm': fontSize.sm,
      '--font-size-base': fontSize.base,
      '--font-size-lg': fontSize.lg,
      '--font-size-xl': fontSize.xl,
      '--font-size-2xl': fontSize['2xl'],
      '--font-size-3xl': fontSize['3xl'],
      '--spacing-xs': spacing.xs,
      '--spacing-sm': spacing.sm,
      '--spacing-md': spacing.md,
      '--spacing-lg': spacing.lg,
      '--spacing-xl': spacing.xl,
      '--spacing-2xl': spacing['2xl'],
      '--border-radius': themeConfig.borderRadius,
      '--box-shadow': themeConfig.boxShadow,
      '--animation-duration': animation.duration,
      '--animation-easing': animation.easing,
    };
  }, [themeConfig]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const cssVariables = getCSSVariables();
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    document.body.style.fontFamily = themeConfig.fontFamily;
  }, [themeConfig, getCSSVariables]);

  const value: ThemeContextType = {
    theme: themeConfig,
    getCSSVariables,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// Hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Utility function to adjust color brightness
export const adjustColor = (color: string, amount: number): string => {
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const getContrastColor = (backgroundColor: string): string => {
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

